import { Decimal } from 'decimal.js'
import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import { money, toMoney } from './money.js'
import type { CustomerRef } from './sales.js'
import { calculateLine, type LineInput } from './sales-calc.js'

/**
 * Loyalty points (ADR-044): an opt-in module the operator switches on per company. Members earn points
 * on posted sales invoices and redeem them at the till as a discount before VAT. Points live in an
 * append-only ledger (`loyalty_entries`); every balance is derived from it, and points expire oldest
 * first.
 */

// ── programme ──────────────────────────────────────────────────────────────────

export const LOYALTY_ROUNDINGS = ['FLOOR', 'NEAREST', 'CEIL'] as const
export type LoyaltyRounding = (typeof LOYALTY_ROUNDINGS)[number]

/** When an invoice's points are credited: once it is posted, or once it is paid in full. */
export const LOYALTY_EARN_ON = ['POSTED', 'PAID'] as const
export type LoyaltyEarnOn = (typeof LOYALTY_EARN_ON)[number]

/**
 * Points that buy OMR 1.000. Each divides 1000, so one point is a whole number of baisa (100 points
 * per rial: a point is worth 10 baisa).
 */
export const LOYALTY_REDEEM_RATES = [
  1, 2, 4, 5, 8, 10, 20, 25, 40, 50, 100, 125, 200, 250, 500, 1000,
] as const

/** Points earned in this many days before today decide a member's tier. */
export const LOYALTY_TIER_WINDOW_DAYS = 365
/** "Expiring soon" on member pages and the overview. */
export const LOYALTY_EXPIRING_SOON_DAYS = 30
export const LOYALTY_MAX_POINTS = 1_000_000

const pointCount = (max = LOYALTY_MAX_POINTS) => z.number().int('Use a whole number').min(0).max(max)

/** A decimal string with up to 2 places within [min, max] (earn rates, multipliers, percentages). */
const decimal2 = (min: number, max: number, message: string) =>
  z
    .string()
    .trim()
    .regex(/^\d{1,7}(\.\d{1,2})?$/, 'Use a number with up to 2 decimals')
    .refine((v) => Number(v) >= min && Number(v) <= max, message)
    .transform((v) => money(v).toFixed(2))

export const loyaltyProgramSchema = z.object({
  /** The company's own switch: off, nobody earns or redeems; balances are kept and can be adjusted. */
  active: z.boolean(),
  name: z.string().trim().min(1, 'Name the programme').max(60),
  nameAr: optionalText(60),
  /** Points per OMR 1.000 of what is earned on. 0 pauses earning at the rate (fixed-point rules still earn). */
  earnRate: decimal2(0, 1000, 'Use 0 to 1000 points'),
  earnOn: z.enum(LOYALTY_EARN_ON),
  /** Earn on the VAT too (default: on the amount before VAT). */
  earnIncludesTax: z.boolean(),
  /** Applied once per invoice. */
  rounding: z.enum(LOYALTY_ROUNDINGS),
  /** Credited points can be spent this many days later (a return window). */
  availableAfterDays: z.number().int().min(0).max(365),
  /** Credited points expire this many months after they became available; 0: never. */
  expiryMonths: z.number().int().min(0).max(120),
  redeemRate: z
    .number()
    .int()
    .refine((v) => (LOYALTY_REDEEM_RATES as readonly number[]).includes(v), 'Choose one of the listed rates'),
  /** The least a member can redeem at once (and must hold to redeem). */
  minRedeemPoints: pointCount(),
  /** The most of a sale (after discounts, before VAT) that points may pay for. */
  maxRedeemPercent: decimal2(0, 100, 'Use 0 to 100 percent'),
  /** Points are redeemed only on sales of at least this much (after discounts, before VAT). */
  minRedeemSale: moneySchema.refine((v) => Number(v) >= 0, 'Cannot be negative').transform((v) => toMoney(v)),
  /** Off: no points on a sale that already has a discount on the whole sale. */
  combineWithDiscounts: z.boolean(),
  /** Granted once, when a customer joins. */
  welcomePoints: pointCount(),
  /** On: every named customer joins on their first purchase. Off: only customers enrolled by hand earn. */
  autoEnroll: z.boolean(),
})
export type LoyaltyProgramInput = z.input<typeof loyaltyProgramSchema>
export type LoyaltyProgramValue = z.output<typeof loyaltyProgramSchema>

export const loyaltyProgramUpdateSchema = loyaltyProgramSchema.extend({ version: versionSchema }).strict()
export type LoyaltyProgramUpdate = z.input<typeof loyaltyProgramUpdateSchema>

export const DEFAULT_LOYALTY_PROGRAM: LoyaltyProgramValue = {
  active: false,
  name: 'Loyalty points',
  nameAr: 'نقاط الولاء',
  earnRate: '1.00',
  earnOn: 'PAID',
  earnIncludesTax: false,
  rounding: 'FLOOR',
  availableAfterDays: 0,
  expiryMonths: 12,
  redeemRate: 100,
  minRedeemPoints: 100,
  maxRedeemPercent: '25.00',
  minRedeemSale: '0.000',
  combineWithDiscounts: true,
  welcomePoints: 0,
  autoEnroll: true,
}

/** GET and PUT /loyalty/program. Never saved: the defaults at version 1. */
export interface LoyaltyProgram extends LoyaltyProgramValue {
  /** What one point is worth (OMR), from the redeem rate. */
  pointValue: string
  version: number
  updatedAt: string | null
  updatedBy: { id: string; name: string } | null
}

/** OMR value of one point: 1 / rate, a whole number of baisa for every listed rate. */
export function loyaltyPointValue(redeemRate: number): string {
  return toMoney(money(1).dividedBy(redeemRate))
}

/** What `count` points are worth at `pointValue` (exact: a point is a whole number of baisa). */
export function pointsValue(count: number, pointValue: string): string {
  return toMoney(money(pointValue).times(count))
}

// ── tiers ──────────────────────────────────────────────────────────────────────

export const loyaltyTierInputSchema = z.object({
  name: z.string().trim().min(1, 'Name the tier').max(40),
  nameAr: optionalText(40),
  /** Points earned on purchases in the last 12 months that reach this tier. */
  minPoints: z.number().int('Use a whole number').min(1, 'Use at least 1 point').max(100_000_000),
  /** Every point earned is multiplied by this (1 to 10). */
  multiplier: decimal2(1, 10, 'Use 1 to 10'),
})
export type LoyaltyTierInput = z.input<typeof loyaltyTierInputSchema>

export const loyaltyTierUpdateSchema = loyaltyTierInputSchema.extend({ version: versionSchema })
export type LoyaltyTierUpdate = z.input<typeof loyaltyTierUpdateSchema>

export interface LoyaltyTierRef {
  id: string
  name: string
  nameAr: string | null
  multiplier: string
}

export interface LoyaltyTier extends LoyaltyTierRef {
  minPoints: number
  /** Active members in the tier today. */
  members: number
  version: number
}

/** The highest tier the qualifying points reach (tiers in any order), or null. */
export function tierFor<T extends { minPoints: number }>(tiers: readonly T[], qualifying: number): T | null {
  let best: T | null = null
  for (const t of tiers) if (qualifying >= t.minPoints && (!best || t.minPoints > best.minPoints)) best = t
  return best
}

/** The next tier up and the points still needed, or null at the top. */
export function nextTierFor<T extends { minPoints: number }>(
  tiers: readonly T[],
  qualifying: number,
): { tier: T; pointsNeeded: number } | null {
  let next: T | null = null
  for (const t of tiers) if (t.minPoints > qualifying && (!next || t.minPoints < next.minPoints)) next = t
  return next ? { tier: next, pointsNeeded: next.minPoints - qualifying } : null
}

// ── bonus rules ────────────────────────────────────────────────────────────────

/** What a rule covers; the most specific rule wins: variant, product, category (nearest), everything. */
export const LOYALTY_RULE_TARGETS = ['ALL', 'CATEGORY', 'PRODUCT', 'VARIANT'] as const
export type LoyaltyRuleTarget = (typeof LOYALTY_RULE_TARGETS)[number]

/** MULTIPLIER: × the earn rate; FIXED: points per unit sold, whatever the price; NONE: earns nothing. */
export const LOYALTY_RULE_MODES = ['MULTIPLIER', 'FIXED', 'NONE'] as const
export type LoyaltyRuleMode = (typeof LOYALTY_RULE_MODES)[number]

const isoDate = z.iso.date()

export const loyaltyRuleInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Name the rule').max(100),
    target: z.enum(LOYALTY_RULE_TARGETS),
    /** The category, product or variant; null for ALL. */
    targetId: z
      .uuid()
      .transform((v) => v.toLowerCase())
      .nullish()
      .transform((v) => v ?? null),
    mode: z.enum(LOYALTY_RULE_MODES),
    multiplier: decimal2(0, 100, 'Use 0 to 100')
      .nullish()
      .transform((v) => v ?? null),
    fixedPoints: pointCount(100_000)
      .nullish()
      .transform((v) => v ?? null),
    /** A promotion's first and last day; none: always. */
    startsOn: isoDate.nullish().transform((v) => v ?? null),
    endsOn: isoDate.nullish().transform((v) => v ?? null),
    active: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if ((v.target === 'ALL') !== (v.targetId === null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['targetId'],
        message: v.target === 'ALL' ? 'A rule for everything names no item' : 'Choose what the rule covers',
      })
    }
    if (v.mode === 'MULTIPLIER' && v.multiplier === null) {
      ctx.addIssue({ code: 'custom', path: ['multiplier'], message: 'Enter the multiplier' })
    }
    if (v.mode === 'FIXED' && v.fixedPoints === null) {
      ctx.addIssue({ code: 'custom', path: ['fixedPoints'], message: 'Enter the points per unit' })
    }
    if (v.startsOn && v.endsOn && v.endsOn < v.startsOn) {
      ctx.addIssue({ code: 'custom', path: ['endsOn'], message: 'The end is before the start' })
    }
  })
  .transform((v) => ({
    ...v,
    multiplier: v.mode === 'MULTIPLIER' ? v.multiplier : null,
    fixedPoints: v.mode === 'FIXED' ? v.fixedPoints : null,
  }))
export type LoyaltyRuleInput = z.input<typeof loyaltyRuleInputSchema>

export const loyaltyRuleUpdateSchema = z.object({ rule: loyaltyRuleInputSchema, version: versionSchema })
export type LoyaltyRuleUpdate = z.input<typeof loyaltyRuleUpdateSchema>

export type LoyaltyRuleStatus = 'ACTIVE' | 'SCHEDULED' | 'ENDED' | 'OFF'

export interface LoyaltyRule {
  id: string
  name: string
  target: LoyaltyRuleTarget
  /** The category, product or variant it covers (null for ALL). */
  targetRef: { id: string; code: string | null; name: string; nameAr: string | null } | null
  mode: LoyaltyRuleMode
  multiplier: string | null
  fixedPoints: number | null
  startsOn: string | null
  endsOn: string | null
  active: boolean
  /** Whether it applies today. */
  status: LoyaltyRuleStatus
  version: number
  createdAt: string
}

export function loyaltyRuleStatus(
  rule: Pick<LoyaltyRule, 'active' | 'startsOn' | 'endsOn'>,
  today: string,
): LoyaltyRuleStatus {
  if (!rule.active) return 'OFF'
  if (rule.startsOn && rule.startsOn > today) return 'SCHEDULED'
  if (rule.endsOn && rule.endsOn < today) return 'ENDED'
  return 'ACTIVE'
}

/** How one invoice line earns. */
export type EarnRule =
  | { mode: 'DEFAULT' }
  | { mode: 'MULTIPLIER'; multiplier: string }
  | { mode: 'FIXED'; fixedPoints: number }
  | { mode: 'NONE' }

/** A rule as rule resolution sees it. */
export interface RuleCandidate {
  id: string
  target: LoyaltyRuleTarget
  targetId: string | null
  mode: LoyaltyRuleMode
  multiplier: string | null
  fixedPoints: number | null
  startsOn: string | null
  endsOn: string | null
  active: boolean
  createdAt: string
}

/**
 * The rule a line earns by on `date`: the variant's, else the product's, else the nearest category's
 * (`categories`: the product's category first, then its parents), else one for everything, else the
 * programme rate. On one target a promotion (with dates) beats a standing rule; then the latest start,
 * then the newest rule.
 */
export function resolveEarnRule(
  candidates: readonly RuleCandidate[],
  line: { variantId: string | null; productId: string | null; categories: readonly string[] },
  date: string,
): { rule: EarnRule; ruleId: string | null } {
  if (!line.variantId) return { rule: { mode: 'NONE' }, ruleId: null }
  const live = candidates.filter((c) => loyaltyRuleStatus(c, date) === 'ACTIVE')
  const pick = (target: LoyaltyRuleTarget, id: string | null) =>
    live
      .filter((c) => c.target === target && c.targetId === id)
      .sort((a, b) => {
        const dated = Number(Boolean(b.startsOn || b.endsOn)) - Number(Boolean(a.startsOn || a.endsOn))
        if (dated) return dated
        const start = (b.startsOn ?? '').localeCompare(a.startsOn ?? '')
        return start || b.createdAt.localeCompare(a.createdAt)
      })[0]
  const found =
    pick('VARIANT', line.variantId) ??
    (line.productId ? pick('PRODUCT', line.productId) : undefined) ??
    line.categories.map((c) => pick('CATEGORY', c)).find(Boolean) ??
    pick('ALL', null)
  if (!found) return { rule: { mode: 'DEFAULT' }, ruleId: null }
  const rule: EarnRule =
    found.mode === 'MULTIPLIER'
      ? { mode: 'MULTIPLIER', multiplier: found.multiplier ?? '1.00' }
      : found.mode === 'FIXED'
        ? { mode: 'FIXED', fixedPoints: found.fixedPoints ?? 0 }
        : { mode: 'NONE' }
  return { rule, ruleId: found.id }
}

// ── earning ────────────────────────────────────────────────────────────────────

/** The terms an invoice earns by, frozen when it is posted. */
export interface EarnTerms {
  earnRate: string
  includeTax: boolean
  rounding: LoyaltyRounding
  /** The member's tier multiplier on the invoice date ("1.00" without a tier). */
  tierMultiplier: string
}

export interface EarnLine {
  /** Ex-VAT, after every discount (the redeemed part included). */
  net: string
  tax: string
  quantity: string
  rule: EarnRule
}

function roundPoints(value: Decimal, rounding: LoyaltyRounding): number {
  const mode =
    rounding === 'FLOOR'
      ? Decimal.ROUND_FLOOR
      : rounding === 'CEIL'
        ? Decimal.ROUND_CEIL
        : Decimal.ROUND_HALF_UP
  return value.toDecimalPlaces(0, mode).toNumber()
}

/**
 * Splits a whole number over weights by the largest remainder (ties to the earlier line), so the parts
 * add up to `total` exactly. Weights of zero get nothing.
 */
export function allocateWhole(total: number, weights: readonly MoneyLike[]): number[] {
  const w = weights.map((x) => money(x))
  const sum = w.reduce((s, x) => s.plus(x), money(0))
  if (total === 0 || !sum.greaterThan(0)) return weights.map(() => 0)
  const exact = w.map((x) => x.times(total).dividedBy(sum))
  const parts = exact.map((x) => x.floor().toNumber())
  let left = total - parts.reduce((s, p) => s + p, 0)
  const order = exact
    .map((x, i) => ({ i, rem: x.minus(x.floor()) }))
    .sort((a, b) => b.rem.comparedTo(a.rem) || a.i - b.i)
  for (const { i } of order) {
    if (left <= 0) break
    parts[i] = (parts[i] ?? 0) + 1
    left -= 1
  }
  return parts
}
type MoneyLike = string | number | Decimal

/**
 * Points an invoice earns: each line's exact points (base × rate × multiplier, or fixed points per
 * unit), times the tier multiplier, rounded once for the invoice and spread back over the lines.
 */
export function calculateEarn(
  lines: readonly EarnLine[],
  terms: EarnTerms,
): { total: number; perLine: number[] } {
  const rate = money(terms.earnRate)
  const exact = lines.map((l) => {
    const base = money(l.net).plus(terms.includeTax ? l.tax : 0)
    const positive = base.greaterThan(0) ? base : money(0)
    switch (l.rule.mode) {
      case 'NONE':
        return money(0)
      case 'FIXED':
        return money(l.rule.fixedPoints).times(l.quantity)
      case 'MULTIPLIER':
        return positive.times(rate).times(l.rule.multiplier)
      default:
        return positive.times(rate)
    }
  })
  const tier = money(terms.tierMultiplier)
  const sum = exact.reduce((s, x) => s.plus(x), money(0)).times(tier)
  const total = Math.max(0, roundPoints(sum, terms.rounding))
  return { total, perLine: allocateWhole(total, exact) }
}

// ── redemption ─────────────────────────────────────────────────────────────────

export const LOYALTY_REDEMPTION_BLOCKS = [
  'PROGRAM_OFF',
  'NOT_MEMBER',
  'MEMBER_BLOCKED',
  'DISCOUNT_CONFLICT',
  'BELOW_MIN_SALE',
  'BELOW_MIN_POINTS',
  'NO_BALANCE',
] as const
export type LoyaltyRedemptionBlock = (typeof LOYALTY_REDEMPTION_BLOCKS)[number]

/** A till sale paying with points: the count, and the point value the till showed (refused if it changed). */
export const loyaltyRedemptionInputSchema = z.object({
  points: z.number().int('Use a whole number').min(1).max(100_000_000),
  pointValue: moneySchema,
})
export type LoyaltyRedemptionInput = z.input<typeof loyaltyRedemptionInputSchema>

export interface RedemptionQuote {
  /** The most this sale can take now (0 when blocked). */
  maxPoints: number
  /** What is redeemed: the request, capped by maxPoints; 0 when refused. */
  points: number
  /** What the points take off (points × point value). */
  amount: string
  pointValue: string
  blocks: LoyaltyRedemptionBlock[]
  /** The request was more than the sale allows and was lowered. */
  reduced: boolean
}

/**
 * What a sale can redeem (the till's preview and the API's check). `goods`: the sale after its discounts,
 * before VAT on the entered price basis; `available`: the member's spendable points.
 */
export function quoteRedemption(input: {
  program: Pick<
    LoyaltyProgramValue,
    | 'active'
    | 'redeemRate'
    | 'minRedeemPoints'
    | 'maxRedeemPercent'
    | 'minRedeemSale'
    | 'combineWithDiscounts'
  >
  member: { status: 'ACTIVE' | 'BLOCKED' } | null
  available: number
  goods: string
  hasSaleDiscount: boolean
  requested: number
}): RedemptionQuote {
  const { program } = input
  const pointValue = loyaltyPointValue(program.redeemRate)
  const blocks: LoyaltyRedemptionBlock[] = []
  if (!program.active) blocks.push('PROGRAM_OFF')
  if (!input.member) blocks.push('NOT_MEMBER')
  else if (input.member.status === 'BLOCKED') blocks.push('MEMBER_BLOCKED')
  if (input.hasSaleDiscount && !program.combineWithDiscounts) blocks.push('DISCOUNT_CONFLICT')
  if (money(input.goods).lessThan(program.minRedeemSale)) blocks.push('BELOW_MIN_SALE')
  // The balance matters only for an active member (a walk-in or a blocked member has its own reason).
  if (
    input.member?.status === 'ACTIVE' &&
    (input.available <= 0 || input.available < program.minRedeemPoints)
  ) {
    blocks.push('NO_BALANCE')
  }
  const capAmount = money(input.goods)
    .times(program.maxRedeemPercent)
    .dividedBy(100)
    .toDecimalPlaces(3, Decimal.ROUND_FLOOR)
  const capPoints = capAmount.dividedBy(pointValue).floor().toNumber()
  let maxPoints = blocks.length ? 0 : Math.max(0, Math.min(input.available, capPoints))
  if (!blocks.length && maxPoints < Math.max(1, program.minRedeemPoints)) {
    blocks.push('BELOW_MIN_POINTS')
    maxPoints = 0
  }
  const requested = Math.max(0, Math.floor(input.requested))
  let count = Math.min(requested, maxPoints)
  if (count > 0 && count < program.minRedeemPoints) {
    if (!blocks.includes('BELOW_MIN_POINTS')) blocks.push('BELOW_MIN_POINTS')
    count = 0
  }
  return {
    maxPoints,
    points: count,
    amount: pointsValue(count, pointValue),
    pointValue,
    blocks,
    reduced: requested > maxPoints && maxPoints > 0,
  }
}

/** A sale's amount after its own discounts, before VAT on the entered basis (what points are measured on). */
export function redemptionBase(lines: readonly LineInput[], pricesIncludeTax: boolean): string {
  return toMoney(
    lines.reduce((s, l) => {
      const c = calculateLine(l, pricesIncludeTax)
      return s.plus(money(c.lineSubtotal).minus(c.discount))
    }, money(0)),
  )
}

/**
 * Takes a redemption off a sale's lines: `amount` is spread over the lines in proportion to what each
 * still costs (cumulative rounding, so the shares add up exactly) and each line's discount becomes one
 * amount (its own discount plus its share). `pointShares` splits the points the same way (whole points,
 * kept on each line for the record); a return measures what it gives back on the money `shares`,
 * rounded once per invoice, not on these.
 */
export function applyRedemption<L extends LineInput>(
  lines: readonly L[],
  amount: string,
  points: number,
  pricesIncludeTax: boolean,
): {
  lines: (Omit<L, 'discountType' | 'discountValue'> & {
    discountType: 'LINE_AMOUNT'
    discountValue: string
  })[]
  shares: string[]
  pointShares: number[]
} {
  const computed = lines.map((l) => calculateLine(l, pricesIncludeTax))
  const bases = computed.map((c) => money(c.lineSubtotal).minus(c.discount))
  const totalBase = bases.reduce((s, b) => s.plus(b), money(0))
  const total = money(amount)
  let running = money(0)
  let previous = money(0)
  const shares = bases.map((b) => {
    if (!totalBase.greaterThan(0)) return '0.000'
    running = running.plus(b)
    const cumulative = money(toMoney(total.times(running).dividedBy(totalBase)))
    const share = cumulative.minus(previous)
    previous = cumulative
    return toMoney(share)
  })
  return {
    lines: lines.map((l, i) => ({
      ...l,
      discountType: 'LINE_AMOUNT' as const,
      discountValue: toMoney(money(computed[i]?.discount ?? 0).plus(shares[i] ?? 0)),
    })),
    shares,
    pointShares: allocateWhole(points, shares),
  }
}

// ── members ────────────────────────────────────────────────────────────────────

export type LoyaltyMemberStatus = 'ACTIVE' | 'BLOCKED'

/** A member's points today, all derived from the ledger. */
export interface LoyaltyBalance {
  /** Spendable now (negative when the member owes points back after a return). */
  available: number
  /** Credited but not spendable yet (the return window). */
  pending: number
  /** Of `available`, what expires within LOYALTY_EXPIRING_SOON_DAYS. */
  expiringSoon: number
  /** Ever earned on purchases, bonuses and additions. */
  earned: number
  /** Ever redeemed, net of points given back. */
  redeemed: number
  expired: number
  /** What `available` is worth today (0 when negative). */
  value: string
}

export interface LoyaltyMemberListItem {
  id: string
  customer: CustomerRef & { phone: string | null }
  status: LoyaltyMemberStatus
  tier: LoyaltyTierRef | null
  available: number
  pending: number
  earned: number
  redeemed: number
  lastActivityAt: string | null
  enrolledAt: string
}

export interface LoyaltyMember extends LoyaltyMemberListItem {
  balance: LoyaltyBalance
  /** Points earned on purchases in the last 12 months (the tier measure). */
  qualifyingPoints: number
  nextTier: { tier: LoyaltyTierRef; pointsNeeded: number } | null
  blockedAt: string | null
  blockedReason: string | null
  version: number
}

export const LOYALTY_MEMBER_SORTS = [
  'name',
  'available',
  'earned',
  'redeemed',
  'lastActivity',
  'enrolledAt',
] as const
export type LoyaltyMemberSort = (typeof LOYALTY_MEMBER_SORTS)[number]

export const loyaltyMemberListQuerySchema = pageQuerySchema.extend({
  status: z.enum(['ACTIVE', 'BLOCKED']).optional(),
  /** positive: points to spend; owing: below zero; zero: none. */
  balance: z.enum(['positive', 'owing', 'zero']).optional(),
  tierId: z.uuid().optional(),
})
export type LoyaltyMemberListQuery = z.input<typeof loyaltyMemberListQuerySchema>

export const loyaltyEnrollSchema = z.object({ customerId: z.uuid() })
export type LoyaltyEnrollInput = z.input<typeof loyaltyEnrollSchema>

export const loyaltyBlockSchema = z.object({
  reason: z.string().trim().min(1, 'Give a reason').max(300),
  version: versionSchema,
})
export type LoyaltyBlockInput = z.input<typeof loyaltyBlockSchema>

export const loyaltyUnblockSchema = z.object({ version: versionSchema })

/**
 * Points added or taken away by hand. `requestId` is made when the dialog opens, so a double submit adds
 * once. A grant expires on `expiresOn`, else by the programme's expiry; a deduction cannot go below zero.
 */
export const loyaltyAdjustSchema = z
  .object({
    points: z
      .number()
      .int('Use a whole number')
      .min(-LOYALTY_MAX_POINTS)
      .max(LOYALTY_MAX_POINTS)
      .refine((v) => v !== 0, 'Enter points to add or take away'),
    reason: z.string().trim().min(3, 'Give a reason').max(500),
    expiresOn: isoDate.nullish().transform((v) => v ?? null),
    requestId: z.uuid(),
  })
  .refine((v) => v.points > 0 || v.expiresOn === null, {
    path: ['expiresOn'],
    message: 'Only added points expire',
  })
export type LoyaltyAdjustInput = z.input<typeof loyaltyAdjustSchema>

/** GET /loyalty/customers/:customerId: the till's and the customer page's view of one customer. */
export interface LoyaltyCustomerStatus {
  customerId: string
  /** Whether the customer can earn: not the walk-in customer, and a member or auto-enrolment on. */
  eligible: boolean
  member: LoyaltyMember | null
}

// ── the ledger ─────────────────────────────────────────────────────────────────

export const LOYALTY_ENTRY_KINDS = [
  'EARN',
  'EARN_REVERSAL',
  'REDEEM',
  'REDEEM_RELEASE',
  'BONUS',
  'ADJUST',
  'EXPIRE',
  /** A write-off given back when a voided return spends the lapsed given-back points again. */
  'EXPIRE_REVERSAL',
] as const
export type LoyaltyEntryKind = (typeof LOYALTY_ENTRY_KINDS)[number]

export interface LoyaltyEntry {
  id: string
  entryDate: string
  kind: LoyaltyEntryKind
  /** Signed. */
  points: number
  /** REDEEM: what the points took off. */
  value: string | null
  availableOn: string | null
  expiresOn: string | null
  member: { id: string; customer: CustomerRef }
  invoice: { id: string; number: string | null } | null
  creditNote: { id: string; number: string | null } | null
  reason: string | null
  createdBy: { id: string; name: string } | null
  createdAt: string
}

export const loyaltyEntryListQuerySchema = pageQuerySchema.extend({
  memberId: z.uuid().optional(),
  kind: z.enum(LOYALTY_ENTRY_KINDS).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
})
export type LoyaltyEntryListQuery = z.input<typeof loyaltyEntryListQuerySchema>

// ── invoices ───────────────────────────────────────────────────────────────────

/** What loyalty did on one invoice (the till receipt, the invoice page). */
export interface LoyaltyInvoiceInfo {
  invoiceId: string
  memberId: string
  programName: string
  programNameAr: string | null
  /** Points the invoice earns now (after returns; 0 once void). */
  earnedPoints: number
  /** PENDING: waits for payment; CREDITED: on the member's account; CANCELLED: the invoice is void. */
  earnStatus: 'PENDING' | 'CREDITED' | 'CANCELLED'
  /** When credited points can be spent (null: at once). */
  availableOn: string | null
  redeemedPoints: number
  redeemedAmount: string
  /** Redeemed points given back on returns or the void. */
  releasedPoints: number
  /** The member's spendable points now. */
  balance: number
}

// ── overview ───────────────────────────────────────────────────────────────────

export const loyaltySummaryQuerySchema = z
  .object({ from: isoDate, to: isoDate })
  .refine((v) => v.from <= v.to, { path: ['to'], message: 'The end is before the start' })
export type LoyaltySummaryQuery = z.input<typeof loyaltySummaryQuerySchema>

export interface LoyaltySummary {
  program: { active: boolean; name: string; nameAr: string | null; pointValue: string }
  /** Today. */
  members: number
  activeMembers: number
  outstandingPoints: number
  /** outstanding × point value: what the points on members' accounts are worth. */
  outstandingValue: string
  /** Points members owe back (balances below zero). */
  owedPoints: number
  pendingPoints: number
  expiringSoon: number
  /** In the period (entry dates). */
  period: {
    from: string
    to: string
    issued: number
    redeemed: number
    redeemedValue: string
    expired: number
    reversed: number
    /** Invoices points were redeemed on. */
    redemptions: number
    newMembers: number
  }
  daily: { date: string; issued: number; redeemed: number; expired: number }[]
  tiers: { tier: LoyaltyTierRef | null; members: number }[]
  topMembers: { id: string; customer: CustomerRef; earned: number }[]
}
